import React from 'react';
import RawResponseModal from '../RawResponseModal/RawResponseModal';
import './CommentsPanel.css';

// Platform colors for badges
const PLATFORM_COLORS = {
  reddit: '#FF4500',
  tiktok: '#000000',
  instagram: '#E4405F',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  youtube: '#FF0000',
  linkedin: '#0A66C2',
  threads: '#000000'
};

/**
 * Pure presentational component for rendering a nested comment thread.
 * No fetching — receives comments[] and onReply/onDelete callbacks.
 *
 * @param {Object} props
 * @param {Array}  props.comments          - Flat array of comment objects (InboxItem shape)
 * @param {Function} props.onReply         - (commentId, text) => Promise
 * @param {Function} props.onDelete        - (commentId) => Promise
 * @param {Function} props.onLoadMoreReplies - (commentId) => Promise, loads more replies for a comment
 * @param {Array|null} props.selectedPlatforms - Platform filter (null = all)
 * @param {boolean}  props.showRawButton   - Whether to show "Raw" button (default true)
 */
const CommentThreadList = ({ comments = [], onReply, onDelete, onLoadMoreReplies, onCommentVisible, selectedPlatforms = null, showRawButton = true }) => {
  const [replyingTo, setReplyingTo] = React.useState(null);
  const [replyText, setReplyText] = React.useState('');
  const [sendingReply, setSendingReply] = React.useState(false);
  const [showRawModal, setShowRawModal] = React.useState(false);
  const [selectedRawResponse, setSelectedRawResponse] = React.useState(null);

  // IntersectionObserver: mark a comment as read only when it actually scrolls into view
  const observerRef = React.useRef(null);
  const observedRef = React.useRef(new Set());

  React.useEffect(() => {
    if (!onCommentVisible) return;
    // Use .chat-window as the observer root so we detect visibility within the scrollable panel
    const chatWindow = document.querySelector('.chat-window');
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const itemId = entry.target.getAttribute('data-comment-id');
            if (itemId && !observedRef.current.has(itemId)) {
              observedRef.current.add(itemId);
              console.log(`[INBOX VISIBLE] Comment visible in panel, marking read: ${itemId}`);
              onCommentVisible(itemId);
            }
          }
        });
      },
      { root: chatWindow, rootMargin: '0px', threshold: 0.1 }
    );
    return () => {
      observerRef.current.disconnect();
      observerRef.current = null;
      observedRef.current.clear();
    };
  }, [onCommentVisible]);

  // After comments change, immediately check which ones are already visible
  // IntersectionObserver only fires on threshold crossing — items already in view
  // when first rendered may not trigger it.
  React.useEffect(() => {
    if (!onCommentVisible || !comments.length) return;
    const checkVisibility = () => {
      const commentEls = document.querySelectorAll('.comment-item[data-comment-id]');
      commentEls.forEach(el => {
        const itemId = el.getAttribute('data-comment-id');
        if (!itemId || observedRef.current.has(itemId)) return;
        const rect = el.getBoundingClientRect();
        const container = document.querySelector('.chat-window');
        if (!container) {
          // No container — check against viewport
          if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
            observedRef.current.add(itemId);
            console.log(`[INBOX VISIBLE] Immediate check: marking already-visible comment read: ${itemId}`);
            onCommentVisible(itemId);
          }
        } else {
          const containerRect = container.getBoundingClientRect();
          if (rect.top >= containerRect.top && rect.bottom <= containerRect.bottom) {
            observedRef.current.add(itemId);
            console.log(`[INBOX VISIBLE] Immediate check: marking already-visible comment read: ${itemId}`);
            onCommentVisible(itemId);
          }
        }
      });
    };
    // Defer to next frame so DOM is painted
    requestAnimationFrame(() => requestAnimationFrame(checkVisibility));
  }, [comments, onCommentVisible]);

  const observeElement = (el) => {
    if (observerRef.current && el) {
      observerRef.current.observe(el);
    }
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await onReply(commentId, replyText);
      setReplyText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Error posting reply:', error);
    } finally {
      setSendingReply(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // O(n) tree build: platform_parent_id references the provider ID, not our
  // local database ID, so use platform_item_id for both sides of the join.
  // Replaces the previous O(n²) recursive filter+map approach.
  const buildNestedComments = (flatComments, rootParentId = null) => {
    const byId = new Map();
    for (const c of flatComments) {
      byId.set(c.platform_item_id, { ...c, replies: c.replies || [] });
    }
    const roots = [];
    for (const node of byId.values()) {
      const parentId = node.platform_parent_id;
      if (parentId === rootParentId || parentId == null) {
        roots.push(node);
      } else {
        const parent = byId.get(parentId);
        if (parent) {
          parent.replies.push(node);
        } else {
          // Orphan — treat as root
          roots.push(node);
        }
      }
    }
    return roots;
  };

  const renderComment = (comment, depth = 0) => {
    const isReplying = replyingTo === comment.id;
    const platformColor = PLATFORM_COLORS[comment.platform] || '#666';
    const hasReplies = comment.replies && comment.replies.length > 0;
    const hasMoreReplies = comment.has_more_children;
    // Backend `has_children` only reflects rows already cached in the DB — for a
    // nested reply, "no child rows yet" does NOT mean "no children" (its replies
    // are lazy-loaded on expand). So nested replies (depth > 0) get an expand
    // button to let the user try — but ONLY while the node is not yet exhausted.
    // Once a fetch reports no next page (`_childrenExhausted`) the button must
    // disappear at every depth, otherwise it is re-clicked and re-fetched
    // forever, duplicating requests and making an exhaustive walk never finish.
    //
    // The provider's own `has_replies` hint (tri-state) removes the guesswork:
    // when the platform explicitly reports a comment has NO replies, never offer
    // the expand button — otherwise every leaf reply shows a useless
    // "Load replies" that fetches nothing.
    const knownNoReplies = comment.has_replies === false;
    const showExpand = onLoadMoreReplies && !comment._childrenExhausted && !knownNoReplies && (
      hasMoreReplies || comment.has_children || comment.has_replies === true || depth > 0
    );

    return (
      <div key={comment.id} className="comment-thread" style={{ marginLeft: depth > 0 ? `${depth * 24}px` : '0' }}>
        <div className="comment-item" ref={observeElement} data-comment-id={comment.platform_item_id}>
          <div className="comment-avatar">
            {comment.sender_avatar_url ? (
              <img src={comment.sender_avatar_url} alt="" />
            ) : (
              <span>{(comment.sender_display_name || comment.sender_username || '?')[0].toUpperCase()}</span>
            )}
          </div>
          <div className="comment-body">
            <div className="comment-header">
              <span className="comment-author">
                {comment.sender_display_name || comment.sender_username || 'Unknown'}
              </span>
              <span
                className="comment-platform-badge"
                style={{ backgroundColor: platformColor }}
              >
                {comment.platform}
              </span>
              <span className="comment-time">{formatTime(comment.platform_created_at || comment.created_at)}</span>
            </div>
            <p className="comment-text">{comment.content}</p>
            <div className="comment-actions">
              <button
                className="comment-reply-btn"
                onClick={() => {
                  setReplyingTo(isReplying ? null : comment.id);
                  setReplyText('');
                }}
              >
                {isReplying ? 'Cancel' : 'Reply'}
              </button>
              {showRawButton && comment.raw_response && (
                <button
                  className="comment-reply-btn"
                  onClick={() => {
                    setSelectedRawResponse(comment.raw_response);
                    setShowRawModal(true);
                  }}
                >
                  Raw
                </button>
              )}
              {onDelete && (
                <button
                  className="comment-delete-btn"
                  onClick={async () => {
                    try {
                      await onDelete(comment.id);
                    } catch (error) {
                      console.error('Error deleting comment:', error);
                    }
                  }}
                >
                  Delete
                </button>
              )}
            </div>
            {isReplying && (
              <div className="reply-input-container">
                <input
                  type="text"
                  placeholder={`Reply to ${comment.sender_username || 'comment'}...`}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleReply(comment.id)}
                  disabled={sendingReply}
                  autoFocus
                />
                <button
                  className="reply-send-btn"
                  onClick={() => handleReply(comment.id)}
                  disabled={!replyText.trim() || sendingReply}
                >
                  {sendingReply ? '...' : 'Send'}
                </button>
              </div>
            )}
          </div>
        </div>
        {hasReplies && (
          <div className="comment-replies">
            {comment.replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
        {showExpand && (
          <div className="expand-replies">
            <button onClick={() => onLoadMoreReplies(comment.id)} disabled={comment._loadingMore}>
              {comment._loadingMore ? 'Loading...' : (hasReplies ? 'Load more replies' : 'Load replies')}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Build nested tree from flat comments
  const allNested = buildNestedComments(comments);

  // Apply platform filter client-side: filter the nested tree (top-level only)
  const filterNested = (nestedList) => {
    if (!selectedPlatforms || selectedPlatforms.length === 0) return nestedList;
    return nestedList.filter(c => selectedPlatforms.includes(c.platform));
  };
  const filteredNested = filterNested(allNested);

  // Recursively count all comments including nested replies
  const countAllComments = (list) => {
    let count = 0;
    for (const c of list) {
      count += 1;
      if (c.replies && c.replies.length > 0) {
        count += countAllComments(c.replies);
      }
    }
    return count;
  };
  const displayedCount = countAllComments(filteredNested);

  return (
    <div className="comments-list">
      {filteredNested.length === 0 ? (
        <div className="comments-empty">
          <p>{comments.length === 0 ? 'No comments yet' : 'No comments match the selected filters'}</p>
        </div>
      ) : (
        <>
          <span className="comments-count">{displayedCount} comment{displayedCount !== 1 ? 's' : ''}</span>
          {filteredNested.map(comment => renderComment(comment))}
        </>
      )}

      <RawResponseModal
        isOpen={showRawModal}
        onClose={() => { setShowRawModal(false); setSelectedRawResponse(null); }}
        rawResponse={selectedRawResponse}
      />
    </div>
  );
};

export default CommentThreadList;
